import * as eaiws from '@egr/wcf/modules/eaiws';
import * as core from '@egr/wcf/modules/core';
import * as cf from '@egr/wcf/modules/cf';
import * as catalog from '@egr/wcf/modules/eaiws/catalog';
import {Log, wcfConfig} from "@egr/wcf/modules/utils";
import {getFilePath} from "@egr/wcf/modules/utils/string";


import {Engine} from "@babylonjs/core";

import {ViewerUI} from './components/viewer';
import {ExportUI} from './components/export';
import {ProgressUI} from './components/progress';
import './index.css';
import {isIOSBrowser} from './utils';


export class Z_Image3dWcfWrapper {

    private elementToAppMap: Map<string, Z_Image3dWcfApp> = new Map();

    constructor() {
        // nothing to do?
    }

    public async insertArticleOnElement(elementId: string, baseArticleNumber: string, variantCode: string): Promise<void> {
        if (!this.elementToAppMap.has(elementId)) {
            const app = new Z_Image3dWcfApp();
            this.elementToAppMap.set(elementId, app);
        }

        const z_Image3dWcfApp: Z_Image3dWcfApp | undefined = this.elementToAppMap.get(elementId);
        if (z_Image3dWcfApp !== undefined) {
            await z_Image3dWcfApp.insertArticleOnElement(elementId, baseArticleNumber, variantCode);
        }
    }
}

export class Z_Image3dWcfApp {

    protected exportUI: ExportUI;

    // private readonly EAIWS_STARTUP: string = 'blanco';
    private readonly EAIWS_STARTUP: string = 'blanco_imp_a2fb2125';
    private readonly EAIWS_URL: string = 'https://configurator.bpro-solutions.com/';

    private viewerUI: ViewerUI;
    private coreApp: core.Application;
    private articleManager: cf.ArticleManager;

    private session: eaiws.EaiwsSession | undefined = undefined;

    constructor() {
        // void this.init();
    }

    public async insertArticleOnElement(elementId: string, baseArticleNumber: string, variantCode: string): Promise<void> {
        ProgressUI.beginLoadingOnElement(elementId);

        if (this.session == undefined) {
            await this.init(elementId);
        }

        if (this.session !== undefined) {
            const searchParameterSet: catalog.SearchArticleParameterSet = new catalog.SearchArticleParameterSet();
            searchParameterSet.catalogIds = ['blanco:0'];
            searchParameterSet.baseArticleNumber = baseArticleNumber;
            searchParameterSet.numberOfHits = 1;
            const lookupOptions: catalog.LookupOptions = new catalog.LookupOptions();
            lookupOptions.displayMode = 'All';
            const foundItems: catalog.TopCatalogItems | undefined = await this.session.catalog.searchArticle(searchParameterSet, lookupOptions);
            const item: catalog.CatalogItem | undefined = foundItems?.scoredItems[0]?.item;
            if (item instanceof catalog.ArticleCatalogItem) {
                // to insert a specific variant, we set the variant code here
                item.varCodeType = 'OFML';
                item.variantCode = variantCode;
                await this.onInsertCatalogArticle(item);
            } else {
                Log.error('Article could not be found.');
            }
        } else {
            Log.error('No session.');
        }

        ProgressUI.endLoadingOnElement(elementId);
    }

    private async init(elementId: string): Promise<void> {
        if (!Engine.isSupported()) {
            alert('WebGL required!');
            return;
        }
        // enable hover effects for icons (like oap-interactors)
        if (!isIOSBrowser() && document.documentElement != null) { // on ios this leads to click icons twice, so we don't add it here
            document.documentElement.classList.add("wcf-hover-enabled");
        }
        // setup w-cf
        // const rootPath: string = getFilePath(window.location.pathname);
        const rootPath: string = getFilePath("/partnerportal/_ui/responsive/common/js/image3d/");
        wcfConfig.libsPath = rootPath + "w-cf/libs/";
        wcfConfig.dataPath = rootPath + "w-cf/data/";

        // create session
        this.session = new eaiws.EaiwsSession();
        const openSessionOption: eaiws.OpenSessionOptions = {startup: this.EAIWS_STARTUP};
        await this.session.open(this.EAIWS_URL, openSessionOption);

        // setup languages (if first language is not available, the second will be used and so on)
        await this.session.catalog.setLanguages(['de', 'en']);
        await this.session.basket.setLanguages(['de', 'en']);

        // setup core
        this.coreApp = new core.Application();
        this.coreApp.applicationName = "WCF-DEV-EXAMPLE";
        this.coreApp.applicationVersion = "1.0.0";
        wcfConfig.dataPath = rootPath + 'w-cf/data/';
        const appOptions: Partial<core.AppInitOptions> = {};
        this.coreApp.initialize(document.getElementById(elementId) as HTMLDivElement, appOptions);
        Log.debug("W-CF " + this.coreApp.version + " build: " + this.coreApp.buildInfo);

        this.articleManager = new cf.ArticleManager(this.coreApp, this.session);
        await this.articleManager.initializeSession({oapPlanningMode: false});
        this.articleManager.setGfjBasketIdsEnabled(true);

        // add all ui components
        this.viewerUI = new ViewerUI(
            this.coreApp,
            {
                disableCameraPanning: false,
                limitCameraDistanceByElementRadius: false
            }
        );

        this.exportUI = new ExportUI(this.articleManager, this.coreApp.viewer, elementId);
    }

    private async onInsertCatalogArticle(article: catalog.ArticleCatalogItem): Promise<void> {
        console.debug('Z_Image3dWcfApp::onInsertCatalogArticle', article);

        const element: cf.MainArticleElement = await this.articleManager.insertArticle(article);

        await this.removeAllElements();
        this.coreApp.model.addElement(element); // we need to add it also to the model manager, or we wont see the new article
        this.coreApp.model.setSelection([element]);
        this.viewerUI.resetCamera();
        this.viewerUI.allowMainArticleSelection(this.coreApp.model.elements.length > 1);
    }

    private async removeAllElements(): Promise<void> {
        while (this.coreApp.model.elements.length > 0) {
            this.coreApp.model.removeElement(this.coreApp.model.elements[0]);
        }
        await this.articleManager.synchronizeSession(false); // we need to tell the server, that we deleted items
    }
}

window.z_Image3dWcfWrapper = new Z_Image3dWcfWrapper(); // make app accessible in console for debug/testing, just type "app" in the browser console